import sanitizeHtml from 'sanitize-html';

import { decodeHtmlEntities } from './decodeHtmlEntities';
import { escapeHtml } from './escapeHtml';
import { parseMathContent } from './parseMathContent';

// Raw 텍스트 입력용 allowlist. 허용되지 않은 태그는 엔티티로 escape되어
// 화면에 보이도록 한다 (조용히 누락되지 않게).
const SANITIZE_OPTIONS: sanitizeHtml.IOptions = {
  allowedTags: ['strong', 'u'],
  allowedAttributes: {},
  disallowedTagsMode: 'escape',
};

// 사전 처리된 HTML(math-inline 태그 포함) 입력용 allowlist.
// idempotent 경로도 sanitize를 통과해야 untrusted 마크업이 섞여 들어와도
// 필터를 우회하지 못한다.
const SANITIZE_OPTIONS_PREPARED: sanitizeHtml.IOptions = {
  allowedTags: ['strong', 'u', 'math-inline'],
  allowedAttributes: {
    'math-inline': ['data-latex', 'data-display'],
  },
  disallowedTagsMode: 'escape',
};

/**
 * Plain text(LaTeX 수식과 일부 인라인 마크 포함 가능)를 Tiptap MathInline
 * extension이 파싱할 수 있는 HTML로 변환한다.
 *
 * 수식 세그먼트는 `<math-inline data-latex="...">`로 감싸고, display 수식
 * (`$$...$$`)은 `data-display="true"` 속성을 추가한다. `<strong>`, `<u>`만
 * 마크로 보존되며 그 외 태그는 엔티티로 escape된다.
 *
 * 입력 전체를 먼저 sanitize한 뒤 수식 분할을 수행하므로, 마크 스팬이 수식
 * 경계를 넘어도 깨지지 않는다 (예: `<strong>속도 $v$</strong>`).
 *
 * @example
 * prepareTiptapHTML("방정식 $x^2$의 해")
 * // -> "방정식 <math-inline data-latex=\"x^2\"></math-inline>의 해"
 *
 * @example
 * prepareTiptapHTML("<strong>중요</strong> $x+1$")
 * // -> "<strong>중요</strong> <math-inline data-latex=\"x+1\"></math-inline>"
 */
export const prepareTiptapHTML = (content: string): string => {
  // 폼 상태 round-trip 등으로 이미 math-inline이 포함된 입력이지만,
  // sanitize는 그대로 거쳐야 함 (idempotent 가드가 보안 우회로가 되지 않게).
  if (content.includes('<math-inline')) {
    return sanitizeHtml(content, SANITIZE_OPTIONS_PREPARED);
  }

  const sanitized = sanitizeHtml(content, SANITIZE_OPTIONS);

  return parseMathContent(sanitized)
    .map(segment => {
      if (segment.type === 'math') {
        const latex = decodeHtmlEntities(segment.content);
        const displayAttr = segment.display ? ' data-display="true"' : '';
        return `<math-inline data-latex="${escapeHtml(
          latex,
        )}"${displayAttr}></math-inline>`;
      }
      return segment.content;
    })
    .join('');
};

/**
 * 문항의 `equation` 필드처럼 수식인 게 확실한 값을 Tiptap HTML 로 바꾼다.
 * `$` 로 감싸면 평문 `$` 와 같은 추론을 거쳐 `abc` 같은 수식이 글자로 떨어질
 * 수 있어, 서버 canonical 과 같은 ZWSP 구분자로 감싸 추론을 건너뛴다.
 */
export const prepareEquationHTML = (equation: string): string =>
  prepareTiptapHTML(`\u200B${equation}\u200B`);
